import { useState } from 'react';
import { RotateCcw, X } from 'lucide-react';
import { Button, Dialog, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { useReviewWriteOff, useWriteOff } from '../model/write-off.queries.js';
import { WriteOffDetail } from './WriteOffDetail.js';

const qtyText = (qty: number): string => String(Math.round(qty * 1000) / 1000);

/**
 * AKT OYNASI — bitta oyna, ikki TOMON uchun.
 *
 * Ilgari bu faqat buxgalternikida bor edi va aktni yuborgan xodim o'z
 * aktining ichini UMUMAN ko'rmasdi: kartochkada raqam, miqdor va surat
 * soni turardi, xolos. Ya'ni «akt qani?», «kim tasdiqladi?», «qaysi
 * talabnomadan?» degan savollarga javob faqat buxgalterda bor edi.
 *
 * Endi ko'rinish BIR XIL (`WriteOffDetail`), farq esa tugmalarda:
 *
 *   `review` — moddiy buxgalter: tasdiqlash va rad etish;
 *   `owner`  — aktning egasi: qayta yuborish va bekor qilish.
 *
 * Rejim tekshiruv EMAS, ko'rinish: haqiqiy to'siq serverda va u har
 * amalda qaytadan so'raladi.
 */
export type WriteOffDialogMode = 'review' | 'owner';

export const WriteOffActDialog = ({
  actId,
  mode,
  onClose,
}: {
  actId: string;
  mode: WriteOffDialogMode;
  onClose: () => void;
}) => {
  const act = useWriteOff(actId);
  const review = useReviewWriteOff();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  const data = act.data;
  const canDecide = mode === 'review' && data?.status === 'PENDING';
  const canResubmit = mode === 'owner' && data?.status === 'REJECTED';
  const canCancel =
    mode === 'owner' && (data?.status === 'PENDING' || data?.status === 'REJECTED');

  /* Sababsiz rad etishni backend ham 400 bilan to'xtatadi, lekin odam
     buni tugmani bosgandan KEYIN bilmasligi kerak. */
  const rejectReady = reason.trim().length > 0;

  const run = (action: 'approve' | 'reject' | 'resubmit' | 'cancel'): void => {
    review.mutate(
      action === 'reject'
        ? { id: actId, action, reason: reason.trim() }
        : { id: actId, action },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={data === undefined ? t('writeOff.title') : data.docNo}
      description={
        data === undefined
          ? undefined
          : `${data.nomenclatureName} — ${qtyText(data.qty)} ${data.unit}`
      }
      size="lg"
      busy={review.isPending}
      error={review.isError ? t('writeOff.loadError') : undefined}
      footer={
        <div className="flex w-full flex-wrap items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={review.isPending}>
            {t('writeOff.close')}
          </Button>

          {canResubmit ? (
            <Button variant="ghost" loading={review.isPending} onClick={() => run('resubmit')}>
              <RotateCcw size={14} aria-hidden />
              {t('writeOff.resubmit')}
            </Button>
          ) : null}

          {canCancel ? (
            <Button variant="ghost" loading={review.isPending} onClick={() => run('cancel')}>
              <X size={14} aria-hidden />
              {t('writeOff.cancel')}
            </Button>
          ) : null}

          {!canDecide ? null : rejecting ? (
            <Button
              variant="danger"
              onClick={() => run('reject')}
              disabled={!rejectReady}
              loading={review.isPending}
            >
              {t('writeOff.reject')}
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setRejecting(true)}>
                {t('writeOff.reject')}
              </Button>
              <Button onClick={() => run('approve')} loading={review.isPending}>
                {t('writeOff.approve')}
              </Button>
            </>
          )}
        </div>
      }
    >
      {data === undefined ? (
        <p className="text-sm text-text-muted">…</p>
      ) : (
        <div className="flex flex-col gap-4">
          <WriteOffDetail data={data} />

          {rejecting ? (
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-text">
                {t('writeOff.rejectReason')}
              </span>
              <textarea
                rows={2}
                value={reason}
                maxLength={500}
                onChange={(event) => setReason(event.target.value)}
                className={cn(
                  'w-full resize-y rounded-[var(--radius-sm)] border border-border-control',
                  'bg-surface px-3 py-2 text-base text-text hover:border-border-strong',
                )}
              />
              <span className="text-xs text-text-muted">{t('writeOff.rejectHint')}</span>
            </label>
          ) : null}
        </div>
      )}
    </Dialog>
  );
};
